import React, {useEffect, useState, FunctionComponent} from "react";
import {makeStyles, createStyles, useTheme, Theme} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import moment from "moment";
import { setTypeForVet } from "../../../Constants/serviceType";
import {
    Typography,
    Card,
    CardActions,
    CardContent,
    Button,
    TextField,
    InputAdornment,
    useMediaQuery,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    FormControl
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import API from "../../../helpers/apiHelpers";
import {useHistory, useLocation} from "react-router-dom";
import {reasonsForVisit} from "../../../Constants/reasonsForVisit";
import {visitOutcomes} from "../../../Constants/visitOutcomes";
import {dogs} from "../../../Constants/dogs";
import {cats} from "../../../Constants/cats";
import {birds} from "../../../Constants/birds";
import {aquatic} from "../../../Constants/aquatic";
import {rodent} from "../../../Constants/rodent";
import {mustelidae} from "../../../Constants/mustelodae";
import {amphibians} from "../../../Constants/amphibians";
import {useFormik} from "formik";
import * as yup from "yup";
import {RootState, useAppDispatch} from "../../../redux/store";
import {useSelector, useDispatch} from "react-redux";
import DatePicker from "../../DatePicker";
import {
    patchAppointment,
    getAppointmentNotes,
    getPrescriptions,
    patchAppointmentNotes,
    setAppointmentNotes
} from "../../../redux/appointment/appointment.actions";
import {editAnimal, setAnimal} from "../../../redux/pets/pets.actions";
import {useSnackbar} from "notistack";
import {setPetAgeVetView, setPetType} from "../../../utils/petInfo";
import useWindowSize from "../../../utils/windowResizeHook";
import {petTypes as hrPetTypes, rhapsodyPetTypes} from "../../../Constants/specialities";
import Box from "@material-ui/core/Box";
import {ReactComponent as LogoSVG} from "../../../Images/logo.svg";
import {insurances, insuranceList} from "../../../Constants/insurance";
import {generalColors, dogsAndCatsColors} from "../../../Constants/colors";
import {IPetFormFields} from "../../../interfaces";
import {createPetTypeIconLookup} from "../../../utils/petTypeIcons";

interface Props {
    data: any,
    isFinished: boolean | null;
    isMobile: boolean;
    setIsFinished: React.Dispatch<any>;
}

const initialValues: IPetFormFields["general"] = {
    name: "",
    type: "",
    breed: "",
    dateOfBirth: null,
    weight: null,
    sex: "",
    neuteredSpayed: "",
    primaryColor: "",
    secondaryColor: "",
    insuranceProvider: "",
    otherInsuranceProvider: "",
    primaryVetName: "",
    primaryVetPhoneNumber: "",
    medications: [],
};

const gender = ["MALE", "FEMALE"];

const useStyle = makeStyles((theme: Theme) => createStyles({
    topSpacing: {
        paddingTop: "20px",
    },
    root: {
        display: "flex",
        width: "90%",
        marginTop: 20,
        alignSelf: "center",
    },
    details: {
        display: "flex",
        flexDirection: "column",
        marginLeft: 10,
        width: "100%"
    },
    content: {
        flex: "auto",
        width: "100%"
    },
    appointmentInfo: {
        width: "100%",
        paddingBottom: 10
    },
}));

//Create the various validation schemas for each form:
//SOAP/NOTES

const PetEdit: FunctionComponent<Props> = ({data, isFinished, setIsFinished, isMobile}: Props) => {
    const [user, setUser] = useState<undefined | any>();
    const [adjustAnimal, setEditAnimal] = useState(false);
    const classes = useStyle();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const appointmentData = useSelector((state: RootState) => state?.appointment.appointment);
    const clinicId = useSelector((state: RootState) => state?.branding.clinicId);
    const [result, setResult] = useState(null);
    const [petImageLink, setPetImageLink] = useState(data.data.item.animal.imageUrl || "");
    const {enqueueSnackbar} = useSnackbar();
    const [animalFormik, setAnimalFormik] = useState<IPetFormFields["general"]>(initialValues);
    const [pcv, setPCV] = useState("no");
    const [clinic, setClinic] = useState("");
    const [pcvPhone, setPCVPhone] = useState("");
    const petTypes = clinicId ? rhapsodyPetTypes : hrPetTypes;

    //Validation if needed:
    const otherValidation = yup.object({
        otherReasonForVisit: yup.string().required("Required field"),
    });
    const getPetBreed = (breed: string) => {
        if (!breed || ["Not listed", "I don't know"].includes(breed)) {
            return "Breed not specified";
        }
        return breed;
    };
    const getPetNeuterStatus = (status: boolean, sex: string) => {
        let statusReturned = "Intact";
        if (sex === "MALE" && status) {
            statusReturned = "Neutered";
            return statusReturned;
        }
        if (sex === "FEMALE" && status) {
            statusReturned = "Spayed";
            return statusReturned;
        }
        return statusReturned;
    };
    const convertStatus = (item: string) => {
        let converted = item === "true" ? true : false;
        return converted;
    };

    //Form Hooks:

    const detailsFormik = useFormik({
        initialValues: animalFormik,
        enableReinitialize: true,
        onSubmit: async (values, {setStatus}) => {
            try {
                const petName = values.name.charAt(0).toUpperCase() + values.name.slice(1).toLowerCase();
                const primary = values.primaryColor ? values.primaryColor.toUpperCase() : "";
                const secondary = values.secondaryColor ? values.secondaryColor.toUpperCase() : "";
                const userInsurance = values.insuranceProvider !== "" ? insurances.filter(item => item.label === values.insuranceProvider)[0].value : null;
                const otherInsurance = values.otherInsuranceProvider !== "" ? values.otherInsuranceProvider : "";

                const body = {
                    ownerId: data.data.item.clientUserId,
                    type: values.type,
                    name: petName,
                    breed: values.breed,
                    dateOfBirth: moment(values.dateOfBirth).format("YYYY-MM-DD"),
                    weight: values.weight,
                    sex: values.sex,
                    neuteredSpayed: values.neuteredSpayed,
                    primaryColor: primary,
                    secondaryColor: secondary,
                    insuranceProvider: userInsurance,
                    otherInsuranceProvider: otherInsurance
                };

                if (pcv === "yes") {
                    body["primaryVetName"] = clinic;
                    body["primaryVetPhoneNumber"] = pcvPhone;
                } else {
                    body["primaryVetName"] = "";
                    body["primaryVetPhoneNumber"] = "";
                }

                await dispatch(editAnimal(data.data.item.animal.id, body));
                setStatus({success: true});
                setEditAnimal(false);
            } catch (error) {
                enqueueSnackbar(error.message || error.errorMessage, {
                    variant: "error",
                    autoHideDuration: 4000,
                });
                setStatus({success: false});
            }
            // }else{
            // 	setEditAnimal(false);
            // }

        },
    });

    const getAnimalValues = () => {
        const animal = data.data.item.animal;
        setAnimalFormik({
            name: animal.name,
            type: (() => {
                const petType = animal.type === "Other" ? animal.breed : animal.type;
                const reg = new RegExp(petType, "i");
                let type = "Other";
                petTypes.forEach(item => {
                    if (reg.test(item.value)) {
                        type = item.value;
                    }
                });
                return type;
            })(),
            breed: animal.breed || "",
            weight: animal.weight || 0,
            sex: animal.sex || "",
            medications: animal.medications || [],
            dateOfBirth: animal.dateOfBirth || null,
            neuteredSpayed: animal.neuteredSpayed && "true" || "false",
            primaryColor: Object.prototype.hasOwnProperty.call(animal, "primaryColor") ? animal["primaryColor"][0] === undefined ? "" : animal["primaryColor"][0] + animal["primaryColor"].slice(1).toLowerCase() : "",
            secondaryColor: Object.prototype.hasOwnProperty.call(animal, "secondaryColor") ? animal["secondaryColor"][0] === undefined ? "" : animal["secondaryColor"][0] + animal["secondaryColor"].slice(1).toLowerCase() : "",
            insuranceProvider: Object.prototype.hasOwnProperty.call(animal, "insuranceProvider") ? insurances.filter(item => item.value === animal["insuranceProvider"])[0].label : "",
            otherInsuranceProvider: Object.prototype.hasOwnProperty.call(animal, "otherInsuranceProvider") ? animal["otherInsuranceProvider"] : "",
            primaryVetName: Object.prototype.hasOwnProperty.call(animal, "primaryVetName") ? animal["primaryVetName"] : "",
            primaryVetPhoneNumber: Object.prototype.hasOwnProperty.call(animal, "primaryVetPhoneNumber") ? animal["primaryVetPhoneNumber"] : "",
        });
        if (Object.prototype.hasOwnProperty.call(animal, "primaryVetName")) {
            setClinic(animal["primaryVetName"]);
            if (animal["primaryVetName"] !== "") {
                setPCV("yes");
            } else {
                setPCV("no");
            }
        }
        if (Object.prototype.hasOwnProperty.call(animal, "primaryVetPhoneNumber")) {
            setPCVPhone(animal["primaryVetPhoneNumber"]);
        }
    };

    //appointment status func
    const requestAppointment = async () => {
        // eslint-disable-next-line no-prototype-builtins
        appointmentData.hasOwnProperty("finished") ? setIsFinished(appointmentData.finished) : setIsFinished(false);
    };

    useEffect(() => {
        (async () => {
            try {
                const resp = await API.getProfiles({userId: data.data.item.clientUserId}).then(response => {
                    setUser(response[0]);
                });
                const animal = await API.getAnimalById(data.data.item.animal.id);
                setPetImageLink(animal.imageUrl);
            } catch (err) {
                console.error(err);
            }
        })();
        requestAppointment();
        if (data?.hasOwnProperty("data")) {
            getAnimalValues();
        }
    }, []);

    const getPetType = (type) => {
        const obj = petTypes.filter(item => {
            return item.value === type;
        });
        if (obj.length) {
            return obj[0].label;
        } else {
            return "";
        }
    };

    const handlePCVChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPCV((event.target as HTMLInputElement).value);
    };

    return (
        <Grid container justify="center" alignItems="center" className={classes.topSpacing} style={{marginBottom: 16}}>
            <Card className={classes.root}>
                {adjustAnimal ? (
                    <form className={classes.details} onSubmit={detailsFormik.handleSubmit}>
                        <CardContent className={classes.content}>
                            <Grid container justify="center" alignItems="center" className={classes.appointmentInfo}>
                                <Grid item xs={8}>
                                    <Typography>{moment(data.data.item.scheduledDate).format("h:mm a")} | {setTypeForVet(data.data.item.type)}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography>Parent: {typeof user !== "undefined" ? user.firstName : null}</Typography>
                                </Grid>
                            </Grid>
                            <TextField
                                value={detailsFormik.values.name}
                                onChange={detailsFormik.handleChange}
                                label="Name"
                                id="name"
                                name="name"
                                aria-label="provide pet name"
                                style={{width: "100%", marginBottom: 10}}
                            />
                            <Autocomplete
                                id="type"
                                aria-label="select pet species"
                                disableClearable
                                value={petTypes.find(type => type.value === detailsFormik.values.type)}
                                onChange={(event, value) => {
                                    //@ts-ignore
                                    value ? detailsFormik.setFieldValue("type", value.value) : detailsFormik.setFieldValue("type", "");
                                }}
                                options={petTypes}
                                getOptionLabel={(option) => option.label}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Type" name="type"/>}
                            />
                            <Autocomplete
                                id="sex"
                                aria-label="select pet sex"
                                disableClearable
                                value={detailsFormik.values.sex}
                                onChange={(event, value) => detailsFormik.setFieldValue("sex", value)}
                                options={gender}
                                getOptionLabel={(option) => option}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Sex" name="sex"/>}
                            />
                            <TextField
                                value={detailsFormik.values.weight}
                                onChange={detailsFormik.handleChange}
                                label="Weight"
                                aria-label="provide pet weight"
                                id="weight"
                                name="weight"
                                style={{width: "100%", marginBottom: 10}}
                                inputProps={{endAdornment: <InputAdornment position="end">lbs.</InputAdornment>}}
                            />
                            {detailsFormik.values.type === "CANINE" &&
                            <Autocomplete
                                options={dogs}
                                id="breed"
                                disableClearable
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.breed}
                                onChange={(event, value) => detailsFormik.setFieldValue("breed", value)}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Breed"/>}
                            />
                            }
                            {detailsFormik.values.type === "FELINE" &&
                            <Autocomplete
                                options={cats}
                                id="breed"
                                aria-label="select pet breed"
                                disableClearable
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.breed}
                                onChange={(event, value) => detailsFormik.setFieldValue("breed", value)}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Breed"/>}
                            />
                            }
                            {detailsFormik.values.type === "AVIAN" &&
                            <Autocomplete
                                options={birds}
                                id="breed"
                                aria-label="select pet breed"
                                disableClearable
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.breed}
                                onChange={(event, value) => detailsFormik.setFieldValue("breed", value)}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Breed"/>}
                            />
                            }
                            {detailsFormik.values.type === "RODENT" &&
                            <Autocomplete
                                options={rodent}
                                id="breed"
                                aria-label="select pet breed"
                                disableClearable
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.breed}
                                onChange={(event, value) => detailsFormik.setFieldValue("breed", value)}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Breed"/>}
                            />
                            }
                            {detailsFormik.values.type === "AQUATIC" &&
                            <Autocomplete
                                options={aquatic}
                                id="breed"
                                disableClearable
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.breed}
                                onChange={(event, value) => detailsFormik.setFieldValue("breed", value)}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Breed"/>}
                            />
                            }
                            {detailsFormik.values.type === "AMPHIBIAN" &&
                            <Autocomplete
                                options={amphibians}
                                id="breed"
                                aria-label="select pet breed"
                                disableClearable
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.breed}
                                onChange={(event, value) => detailsFormik.setFieldValue("breed", value)}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Breed"/>}
                            />
                            }
                            {detailsFormik.values.type === "MUSTELIDAE" &&
                            <Autocomplete
                                options={mustelidae}
                                id="breed"
                                aria-label="select pet breed"
                                disableClearable
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.breed}
                                onChange={(event, value) => detailsFormik.setFieldValue("breed", value)}
                                style={{marginBottom: 10}}
                                renderInput={(params) => <TextField {...params} label="Breed"/>}
                            />
                            }
                            {detailsFormik.values.type === "OTHER" &&
                            <TextField
                                id="breed"
                                aria-label="select pet breed"
                                label="Breed"
                                style={{width: "100%", marginBottom: 10}}
                                value={detailsFormik.values.breed}
                                onChange={detailsFormik.handleChange}
                            />
                            }
                            <TextField
                                id="dateOfBirth"
                                name="dateOfBirth"
                                type="date"
                                label="Date of Birth"
                                aria-label="select pet date of birth"
                                InputProps={{inputProps: {max: moment().format("YYYY-MM-DD")} }}
                                style={{width: "100%", marginBottom: 10}}
                                value={detailsFormik.values.dateOfBirth}
                                onChange={detailsFormik.handleChange}
                            />
                            <FormControl component="fieldset" style={{display: "flex"}}>
                                <FormLabel
                                    component="legend">{detailsFormik.values.sex === "FEMALE" ? "Spayed" : "Neutered"}</FormLabel>
                                <RadioGroup row name="neuteredSpayed"
                                    value={convertStatus(detailsFormik.values.neuteredSpayed)}
                                    onChange={(event, value) => {
                                        detailsFormik.setFieldValue("neuteredSpayed", value.toString());
                                    }}
                                >
                                    <FormControlLabel value={true} control={<Radio/>} label="Yes"/>
                                    <FormControlLabel value={false} control={<Radio/>} label="No"/>
                                </RadioGroup>
                            </FormControl>
                            {(detailsFormik.values.type === "Dog" || detailsFormik.values.type === "Cat") &&
                            <Autocomplete
                                fullWidth
                                options={dogsAndCatsColors}
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.primaryColor}
                                onChange={(event, value) => {
                                    detailsFormik.setFieldValue("primaryColor", value);
                                }}
                                renderInput={(params) => <TextField {...params} label="Primary Color" margin="dense"/>}
                            />
                            }
                            {(detailsFormik.values.type !== "Cat" && detailsFormik.values.type !== "Dog") &&
                            <Autocomplete
                                fullWidth
                                options={generalColors}
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.primaryColor}
                                onChange={(event, value) => {
                                    detailsFormik.setFieldValue("primaryColor", value);
                                }}
                                renderInput={(params) => <TextField {...params} label="Primary Color" margin="dense"/>}
                            />
                            }
                            {(detailsFormik.values.type === "Dog" || detailsFormik.values.type === "Cat") &&
                            <Autocomplete
                                fullWidth
                                options={dogsAndCatsColors}
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.secondaryColor}
                                onChange={(event, value) => {
                                    detailsFormik.setFieldValue("secondaryColor", value);
                                }}
                                renderInput={(params) => <TextField {...params} label="Secondary Color"
                                    margin="dense"/>}
                            />
                            }
                            {(detailsFormik.values.type !== "Cat" && detailsFormik.values.type !== "Dog") &&
                            <Autocomplete
                                fullWidth
                                options={generalColors}
                                getOptionLabel={(option) => option}
                                value={detailsFormik.values.secondaryColor}
                                onChange={(event, value) => {
                                    detailsFormik.setFieldValue("secondaryColor", value);
                                }}
                                renderInput={(params) => <TextField {...params} label="Secondary Color"
                                    margin="dense"/>}
                            />
                            }
                            <Autocomplete
                                fullWidth
                                options={insuranceList}
                                getOptionLabel={(option) => option}
                                style={{width: "100%", marginBottom: 10}}
                                value={detailsFormik.values.insuranceProvider}
                                onChange={(event, value) => {
                                    value ? detailsFormik.setFieldValue("insuranceProvider", value) : detailsFormik.setFieldValue("insuranceProvider", "");
                                }}
                                renderInput={(params) => <TextField {...params} margin="dense"
                                    label="Insurance Provider"/>}
                            />
                            {detailsFormik.values.insuranceProvider === "Other" &&
                            <TextField
                                id="otherInsuranceProvider"
                                name="otherInsuranceProvider"
                                label="Other Insurance Provider"
                                value={detailsFormik.values.otherInsuranceProvider}
                                style={{width: "100%", marginBottom: 10}}
                                variant="outlined"
                                color="secondary"
                                type="text"
                                margin="dense"
                                onChange={detailsFormik.handleChange}
                            />
                            }
                            <FormControl component="fieldset" style={{display: "flex"}}>
                                <FormLabel component="legend">Primary Care Vet</FormLabel>
                                <RadioGroup name="pcv" value={pcv} row onChange={handlePCVChange}>
                                    <FormControlLabel value="Yes" control={<Radio/>} label="Yes"/>
                                    <FormControlLabel value="no" control={<Radio/>} label="No"/>
                                </RadioGroup>
                            </FormControl>

                            {pcv === "yes" &&
                            <>
                                <TextField
                                    value={clinic}
                                    onChange={(e) => setClinic(e.target.value)}
                                    variant="outlined"
                                    color="secondary"
                                    type="text"
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name="clinic"
                                    label="Clinic Name"
                                />
                                <TextField
                                    value={pcvPhone}
                                    onChange={(e) => setPCVPhone(e.target.value)}
                                    variant="outlined"
                                    color="secondary"
                                    type="text"
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name="phoneNumber"
                                    label="Phone Number"
                                />
                            </>
                            }
                        </CardContent>
                        <CardActions>
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                type="submit"
                            >
                                Save Details
                            </Button>
                        </CardActions>
                    </form>
                ) : (
                    <div className={classes.details}>
                        <CardContent className={classes.content}>
                            <Grid container alignItems="center" className={classes.appointmentInfo}>
                                <Grid item xs={6}>
                                    <Typography
                                        variant="body2">{data?.hasOwnProperty("data") ? moment(data.data.item.scheduledDate).format("h:mm a") : null} | {data?.hasOwnProperty("data") ? setTypeForVet(data.data.item.type) : null}</Typography>
                                </Grid>
                            </Grid>
                            <Grid container justify={isMobile ? "flex-start" : "space-between"} alignItems="center">
                                <Grid item xs={12} sm={2} md={3}>
                                    <Box position="relative" width="fit-content">
                                        {
                                            petImageLink ?
                                                <img
                                                    alt="pet image"
                                                    src={petImageLink}
                                                    onError={() => setPetImageLink("")}
                                                    width="80"
                                                    height="80"
                                                    style={{borderRadius: "4px", objectFit: "cover"}}
                                                />
                                                :
                                                createPetTypeIconLookup({width: 80, height: 80})[setPetType(detailsFormik.values.type)] ||
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    width="80px"
                                                    height="80px"
                                                    borderRadius="4px"
                                                >
                                                    <LogoSVG/>
                                                </Box>
                                        }
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={9} md={8}>
                                    <Typography variant="h4" component="h2" gutterBottom style={{wordBreak: "break-all"}}>
                                        {detailsFormik.values.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        {
                                            `${setPetAgeVetView(detailsFormik.values.dateOfBirth)} | 
                                            ${detailsFormik.values.sex?.charAt(0)}${detailsFormik.values.sex?.slice(1).toLowerCase()} |
                                            ${getPetNeuterStatus(convertStatus(detailsFormik.values.neuteredSpayed), detailsFormik.values.sex)}`
                                        }
                                    </Typography>
                                    <Typography variant="body2">
                                        {
                                            `${getPetType(detailsFormik.values.type)} | 
                                            ${getPetBreed(detailsFormik.values.breed)} |   
                                            ${detailsFormik.values.weight} lbs`
                                        }
                                    </Typography>
                                    <Typography variant="body2">
                                        {
                                            `${detailsFormik.values.primaryColor} |    
                                            ${detailsFormik.values.secondaryColor} coloring`
                                        }
                                    </Typography>
                                    <Typography variant="body2">
                                        {!detailsFormik.values.insuranceProvider || detailsFormik.values.insuranceProvider === "I don't have it"
                                            ? "No Insurance"
                                            : (detailsFormik.values.insuranceProvider !== "Other"
                                                ? detailsFormik.values.insuranceProvider  + " Insurance"
                                                : detailsFormik.values.otherInsuranceProvider + " Insurance")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {clinic.length ? `Clinic: ${clinic}` : "No PCV Provided"}
                                    </Typography>
                                    {pcvPhone.length ?
                                        <Typography variant="body2">
                                            Phone: {pcvPhone}
                                        </Typography>: null}
                                </Grid>
                            </Grid>
                        </CardContent>
                        <CardActions>
                            <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => setEditAnimal(true)}
                            >
                                Edit Details
                            </Button>
                        </CardActions>
                    </div>
                )}
            </Card>
        </Grid>
    );
};

export default PetEdit;
